import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ConfirmWhtRemittance,
  CreateWhtCertificate,
  CreateWhtFiling,
  UpdateWhtCertificate,
  UpdateWhtFiling,
  WhtCertificate,
  WhtFiling,
  WhtFormType,
  WhtSummary,
} from '@dcc-erp/shared';

import { api, apiBlob, apiJson } from '../api';

// Cache-key scopes keyed by companyId (same pattern as ar.ts).
const certsKey = (companyId: string | undefined) => ['wht-certs', companyId] as const;
const filingsKey = (companyId: string | undefined) => ['wht-filings', companyId] as const;
const summaryKey = (companyId: string | undefined) => ['wht-summary', companyId] as const;

export interface WhtCertFilters {
  period?: string;
  formType?: WhtFormType;
  vendorId?: string;
}

export function useWhtCerts(companyId: string | undefined, filters: WhtCertFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.period) qs.set('period', filters.period);
  if (filters.formType) qs.set('formType', filters.formType);
  if (filters.vendorId) qs.set('vendorId', filters.vendorId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...certsKey(companyId), filters],
    queryFn: () =>
      api<WhtCertificate[]>(`/companies/${companyId}/wht/certs${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useWhtCert(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['wht-certs', companyId, id],
    queryFn: () => api<WhtCertificate>(`/companies/${companyId}/wht/certs/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateWhtCert(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWhtCertificate) =>
      apiJson<CreateWhtCertificate, WhtCertificate>(
        'POST',
        `/companies/${companyId}/wht/certs`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: certsKey(companyId) });
      void qc.invalidateQueries({ queryKey: summaryKey(companyId) });
    },
  });
}

export function useUpdateWhtCert(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWhtCertificate }) =>
      apiJson<UpdateWhtCertificate, WhtCertificate>(
        'PATCH',
        `/companies/${companyId}/wht/certs/${id}`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: certsKey(companyId) });
      void qc.invalidateQueries({ queryKey: summaryKey(companyId) });
    },
  });
}

export function useWhtFilings(companyId: string | undefined, year?: number) {
  const qs = new URLSearchParams();
  if (year !== undefined) qs.set('year', String(year));
  const search = qs.toString();
  return useQuery({
    queryKey: [...filingsKey(companyId), { year: year ?? null }],
    queryFn: () =>
      api<WhtFiling[]>(`/companies/${companyId}/wht/filings${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useCreateWhtFiling(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWhtFiling) =>
      apiJson<CreateWhtFiling, WhtFiling>('POST', `/companies/${companyId}/wht/filings`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: filingsKey(companyId) });
      void qc.invalidateQueries({ queryKey: certsKey(companyId) });
    },
  });
}

// File one DRAFT WHT filing per tax-branch for (period, formType) — Phase 42
// optional-2. Returns the per-branch filings created.
export function useFileWhtBranches(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ period, formType }: { period: string; formType: WhtFormType }) =>
      apiJson<undefined, WhtFiling[]>(
        'POST',
        `/companies/${companyId}/wht/filings/branches?period=${period}&formType=${formType}`,
        undefined,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: filingsKey(companyId) });
      void qc.invalidateQueries({ queryKey: certsKey(companyId) });
    },
  });
}

export function useUpdateWhtFiling(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWhtFiling }) =>
      apiJson<UpdateWhtFiling, WhtFiling>(
        'PATCH',
        `/companies/${companyId}/wht/filings/${id}`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: filingsKey(companyId) });
      void qc.invalidateQueries({ queryKey: certsKey(companyId) });
    },
  });
}

// ===== File downloads (B4) — plain async fns, not react-query (they stream
// files and trigger a browser download rather than caching a value). =====

// GET .../wht/certs/:id/pdf — opens the 50ทวิ PDF in a new tab.
// The blob URL is revoked after 60 s so the opened tab has time to load the
// PDF before the object URL is released (mirrors the pattern in downloadFilingTxt
// but with a delay to keep the tab alive).
export async function downloadCertPdf(companyId: string, certId: string): Promise<void> {
  const blob = await apiBlob(`/companies/${companyId}/wht/certs/${certId}/pdf`);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// GET .../wht/filings/:id/export.txt — downloads the RD e-filing TXT.
export async function downloadFilingTxt(companyId: string, filingId: string): Promise<void> {
  const blob = await apiBlob(`/companies/${companyId}/wht/filings/${filingId}/export.txt`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pnd-${filingId}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

// PATCH .../wht/certs/:id/confirm-remittance (B5) — books the e-WHT
// WHT_PAYABLE clearing JV and stamps the bank's remittance proof. Only
// applicable to channel=E_WHT certs; 400 for NORMAL, 409 if already confirmed.
export function useConfirmWhtRemittance(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConfirmWhtRemittance }) =>
      apiJson<ConfirmWhtRemittance, WhtCertificate>(
        'PATCH',
        `/companies/${companyId}/wht/certs/${id}/confirm-remittance`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: certsKey(companyId) });
    },
  });
}

export function useWhtSummary(
  companyId: string | undefined,
  period: string | undefined,
  formType: WhtFormType | undefined,
) {
  return useQuery({
    queryKey: [...summaryKey(companyId), { period: period ?? null, formType: formType ?? null }],
    queryFn: () =>
      api<WhtSummary>(`/companies/${companyId}/wht/summary?period=${period}&formType=${formType}`),
    enabled: !!companyId && !!period && !!formType,
  });
}
